import DownloadIcon from "@mui/icons-material/Download"
import {
  Alert,
  Button,
  Card,
  CardContent,
  Stack,
  Typography,
} from "@mui/material"
import { DataGrid, GridColDef } from "@mui/x-data-grid"
import { useMemo, useState } from "react"
import { DateRangeFilter } from "../components/DateRangeFilter"
import {
  GroupFilterSelect,
  useGroupFilter,
} from "../components/GroupFilterSelect"
import { ReportPageHeader } from "../components/ReportPageHeader"
import { userEventCreditTypeReportPage } from "../constants/reportPages"
import {
  exportUserEventCreditTypeAsCSV,
  getPaginationMeta,
  GetUserEventCreditTypeParams,
  useGetUserEventCreditTypeQuery,
} from "../services/api"
import { useAppSelector } from "../store/hooks"
import { selectActiveOrgId } from "../store/sessionSlice"

type UserEventCreditTypeDateFilters = Pick<
  GetUserEventCreditTypeParams,
  "start_date" | "end_date"
>

function formatHours(value: number | null | undefined) {
  if (value === null || value === undefined) return "-"
  return Number.isInteger(value) ? String(value) : value.toFixed(2)
}

function UserEventCreditTypePage() {
  const activeOrgId = useAppSelector(selectActiveOrgId)
  const { selectedGroups, setSelectedGroups, groupParams } =
    useGroupFilter(activeOrgId)
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  })
  const [validationError, setValidationError] = useState<string | null>(null)
  const [filters, setFilters] = useState({
    start_date: "",
    end_date: "",
  })
  const [appliedFilters, setAppliedFilters] = useState<
    Partial<UserEventCreditTypeDateFilters>
  >({})

  const queryParams = {
    ...appliedFilters,
    ...groupParams,
    limit: paginationModel.pageSize,
    offset: paginationModel.page * paginationModel.pageSize,
    org_id: activeOrgId,
  }

  const { data, error, isLoading } = useGetUserEventCreditTypeQuery(queryParams)
  const paginationMeta = getPaginationMeta(data)

  const columns: GridColDef[] = useMemo(() => {
    const baseColumns: GridColDef[] = [
      {
        field: "userName",
        headerName: "User Name",
        width: 220,
        sortable: false,
        valueGetter: (_value, row) => {
          const firstName = row.user?.first_name || ""
          const lastName = row.user?.last_name || ""
          return `${firstName} ${lastName}`.trim() || "-"
        },
      },
      {
        field: "userEmail",
        headerName: "Email",
        width: 260,
        sortable: false,
        valueGetter: (_value, row) => row.user?.email || "-",
      },
      {
        field: "event_count",
        headerName: "Event Count",
        width: 130,
        type: "number",
        sortable: false,
      },
    ]

    const taxonomyKeys = Array.from(
      new Set(
        (data?.data || []).flatMap((row) =>
          Object.keys(row.credit_type_hours || {})
        )
      )
    ).sort((a, b) => a.localeCompare(b))

    const taxonomyColumns: GridColDef[] = taxonomyKeys.map((taxonomyName) => ({
      field: `credit_type_hours:${taxonomyName}`,
      headerName: taxonomyName,
      width: 180,
      sortable: false,
      valueGetter: (_value, row) =>
        formatHours(row.credit_type_hours?.[taxonomyName] ?? 0),
    }))

    return [...baseColumns, ...taxonomyColumns]
  }, [data?.data])

  const handleFilterChange = (
    field: "start_date" | "end_date",
    value: string
  ) => {
    setFilters((prev) => ({ ...prev, [field]: value }))
  }

  const handleApplyFilters = () => {
    const nextFilters: Partial<UserEventCreditTypeDateFilters> = {}
    const startMs = filters.start_date ? Date.parse(filters.start_date) : null
    const endMs = filters.end_date ? Date.parse(filters.end_date) : null

    if (startMs !== null && Number.isNaN(startMs)) {
      setValidationError("Start date is invalid.")
      return
    }

    if (endMs !== null && Number.isNaN(endMs)) {
      setValidationError("End date is invalid.")
      return
    }

    if (startMs !== null && endMs !== null && startMs > endMs) {
      setValidationError("Start date must be before end date.")
      return
    }

    if (startMs !== null) {
      nextFilters.start_date = new Date(startMs).toISOString()
    }

    if (endMs !== null) {
      nextFilters.end_date = new Date(endMs).toISOString()
    }

    setValidationError(null)
    setPaginationModel((prev) => ({ ...prev, page: 0 }))
    setAppliedFilters(nextFilters)
  }

  const handleClearFilters = () => {
    setSelectedGroups([])
    setValidationError(null)
    setAppliedFilters({})
    setPaginationModel({ page: 0, pageSize: 25 })
    setFilters({
      start_date: "",
      end_date: "",
    })
  }

  const handleExportCSV = () => {
    const exportParams = {
      ...appliedFilters,
      ...groupParams,
      org_id: activeOrgId,
    }
    exportUserEventCreditTypeAsCSV(exportParams)
  }

  return (
    <Stack spacing={4}>
      <ReportPageHeader
        title={userEventCreditTypeReportPage.pageTitle}
        description={userEventCreditTypeReportPage.endpointDescription}
        action={
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleExportCSV}
            disabled={isLoading}
          >
            Export CSV
          </Button>
        }
      />

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Filters
          </Typography>
          <Stack
            direction="row"
            spacing={2}
            flexWrap="wrap"
            alignItems="flex-end"
          >
            <GroupFilterSelect
              organizationId={activeOrgId}
              value={selectedGroups}
              onChange={(value) => {
                setSelectedGroups(value)
                setPaginationModel((prev) => ({ ...prev, page: 0 }))
              }}
            />
            <DateRangeFilter
              label="Event Date Range"
              startDate={filters.start_date}
              endDate={filters.end_date}
              onStartDateChange={(value) =>
                handleFilterChange("start_date", value)
              }
              onEndDateChange={(value) => handleFilterChange("end_date", value)}
            />
            <Button variant="contained" onClick={handleApplyFilters}>
              Apply
            </Button>
            <Button variant="outlined" onClick={handleClearFilters}>
              Clear
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {validationError && <Alert severity="warning">{validationError}</Alert>}

      {error && (
        <Alert severity="error">
          Error loading credit type data. Please try again.
        </Alert>
      )}

      <Card>
        <DataGrid
          rows={data?.data || []}
          getRowId={(row) => row.user.id}
          columns={columns}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          rowCount={paginationMeta?.total || paginationMeta?.count || 0}
          paginationMode="server"
          loading={isLoading}
          pageSizeOptions={[10, 25, 50, 100]}
          disableRowSelectionOnClick
          autoHeight
        />
      </Card>
    </Stack>
  )
}

export default UserEventCreditTypePage
